import type { LinearRGB } from '../core/color';
import { clamp, smoothstep } from '../core/math';

/*
 * TypeScript mirror of the painter's open water in water.frag and openWater.glsl (STYLE §7).
 *
 * The painting's IJ lightens from L* 28 where the painter view looks down on it most steeply (48 deg
 * below the horizon) to L* 43-45 at 39-40 deg, and turns a little yellower and greener on the way.
 * Measured on the museum's scan area-averaged to the 2000 px frame, on the IJ's water more than 40 m
 * from any shore with vessels and 3 px about them left out by colour (openWaterPatches.ts), in
 * 0.5 deg bins of the grazing angle each fragment is seen at, against a painter-view render: the
 * water is a mix of two colours, the steep one below 48 deg and the shallow one above 40.13 deg,
 * the share of the shallow one falling as a smoothstep between. Both colours and the shallow end
 * are fitted (rms 0.99 dE over the bins, weighted by their pixels, against 2.96 for the sky mirror
 * it replaces); the steep end is held at 48 deg, the steepest water the painter view shows, so
 * neither colour lies beyond the painted angles. A lightness threshold for vessels (L* < 28) had
 * taken the painting's darkest water out with them and shown its near water 2 L* too light.
 *
 * Kind codes below 1.5 are the IJ and its harbour with a sharp edge to the other kinds; the IJ
 * alone (below 0.5) ramps across whole triangles at the Oudezijds Kolk mouth. The free views keep
 * the round-7 fit's darkening of the open water's body (waterCues.ts).
 */
export const OPEN_WATER = {
  kindLimit: 1.5,
  freeBaseScale: 0.782,
  /** Lab 28.42 / -10.91 / 14.55. */
  steep: [0.042523, 0.063331, 0.025405] as LinearRGB,
  /** Lab 42.68 / -14.21 / 16.70. */
  shallow: [0.095293, 0.145977, 0.066057] as LinearRGB,
  shallowDeg: 40.13,
  steepDeg: 48.0,
} as const;

const RAD_TO_DEG = 180 / Math.PI;

/** How much of the shallow colour the open water takes at this grazing angle. */
export function openWaterShallowShare(grazingDeg: number): number {
  return 1 - smoothstep(OPEN_WATER.shallowDeg, OPEN_WATER.steepDeg, grazingDeg);
}

/** The painter's open water seen along this view, before its shift (openWaterPatches.ts). */
export function openWaterAlbedo(view: readonly [number, number, number]): LinearRGB {
  const grazingDeg = Math.asin(clamp(-view[1] / Math.hypot(...view), -1, 1)) * RAD_TO_DEG;
  const t = openWaterShallowShare(grazingDeg);
  const { steep, shallow } = OPEN_WATER;
  return [
    steep[0] + (shallow[0] - steep[0]) * t,
    steep[1] + (shallow[1] - steep[1]) * t,
    steep[2] + (shallow[2] - steep[2]) * t,
  ];
}
